import React, { useEffect, useState } from "react";
import { Box, Typography, TextField, Button, CircularProgress, Alert } from "@mui/material";
import { fetchMyProfile, updateMyProfile, fetchCountries, fetchLanguages } from "../../Services/apiService";
import { useUnsavedChanges } from "./unsavedChanges";
import { useTranslation, useLanguage } from "../../i18n/LanguageContext";
import { useAuth } from "../../authContext/authContext";
import SearchableSelect from "../SearchableSelect";

const fieldSx = {
  width: 280,
  "& .MuiOutlinedInput-root": {
    borderRadius: 1.5,
    fontSize: "0.8125rem",
    bgcolor: "#EBEBEB",
    "& fieldset": { borderColor: "#EBEBEB" },
    "&:hover fieldset": { borderColor: "#d8d8d8" },
    "&.Mui-focused fieldset": { borderColor: "#cfcfcf" },
  },
};

const actionButtonSx = {
  textTransform: "none",
  borderRadius: 1.5,
  px: 3,
  py: 0.5,
  minHeight: 0,
  fontSize: "0.8125rem",
  color: "#1a1a1a",
  bgcolor: "#EBEBEB",
  boxShadow: "none",
  "&:hover": { bgcolor: "#E0E0E0", boxShadow: "none" },
};

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function Row({ label, children }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 3,
        py: 2,
        borderBottom: "1px solid #e5e7eb",
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Typography sx={{ fontSize: "0.8125rem", color: "#111827" }}>{label}</Typography>
      <Box sx={{ flexShrink: 0, display: "flex", justifyContent: "flex-end" }}>{children}</Box>
    </Box>
  );
}

export default function GeneralSettings() {
  const { t } = useTranslation();
  const { setLanguage } = useLanguage();
  const { updateUserFields } = useAuth();
  const [profile, setProfile] = useState(null);
  const [name, setName] = useState("");
  const [countryId, setCountryId] = useState(null);
  const [languageId, setLanguageId] = useState(null);
  const [countries, setCountries] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [referenceDataLoading, setReferenceDataLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError("");
    fetchMyProfile()
      .then((res) => {
        if (cancelled) return;
        const data = res?.data;
        setProfile(data);
        setName(data?.name || "");
        setCountryId(data?.country_id ?? null);
        setLanguageId(data?.language_id ?? null);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err.message || t("settings.general.loadError"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([fetchCountries(), fetchLanguages()])
      .then(([countriesRes, languagesRes]) => {
        if (cancelled) return;
        setCountries(countriesRes?.data || []);
        setLanguages(languagesRes?.data || []);
      })
      .finally(() => {
        if (!cancelled) setReferenceDataLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const dirty =
    profile != null &&
    (name !== (profile.name || "") ||
      countryId !== (profile.country_id ?? null) ||
      languageId !== (profile.language_id ?? null));

  const handleSave = async () => {
    setSaving(true);
    setSaveError("");
    setSaved(false);
    try {
      const res = await updateMyProfile({ name, countryId, languageId });
      setProfile(res.data);
      setSaved(true);

      if (res.data?.language_id !== profile?.language_id && res.data?.language_code) {
        updateUserFields?.({
          languageId: res.data.language_id,
          languageCode: res.data.language_code,
          languageName: res.data.language_name,
        });
        setLanguage(res.data.language_code);
      }
      return true;
    } catch (err) {
      setSaveError(err.message || t("settings.general.saveError"));
      return false;
    } finally {
      setSaving(false);
    }
  };

  useUnsavedChanges("general", dirty, handleSave);

  return (
    <Box sx={{ px: 1, pt: 0.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, mb: 1.5 }}>
        <Typography sx={{ fontSize: "1.125rem", fontWeight: 600, color: "#111827" }}>
          {t("settings.general.title")}
        </Typography>

        {!loading && !loadError && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {saveError && (
              <Alert severity="error" sx={{ fontSize: "0.8125rem", py: 0 }}>
                {saveError}
              </Alert>
            )}
            {saved && !saveError && (
              <Alert severity="success" sx={{ fontSize: "0.8125rem", py: 0 }}>
                {t("settings.general.profileUpdated")}
              </Alert>
            )}
            {dirty && (
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || !name.trim() || !countryId || !languageId}
                sx={actionButtonSx}
              >
                {saving ? <CircularProgress size={16} sx={{ color: "#1a1a1a" }} /> : t("settings.general.saveChanges")}
              </Button>
            )}
          </Box>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : loadError ? (
        <Alert severity="error" sx={{ fontSize: "0.8125rem" }}>
          {loadError}
        </Alert>
      ) : (
        <>
          <Box>
            <Row label={t("settings.general.fullName")}>
              <TextField
                value={name}
                onChange={(e) => setName(e.target.value)}
                size="small"
                sx={fieldSx}
              />
            </Row>
            <Row label={t("settings.general.country")}>
              <SearchableSelect
                value={countryId}
                onChange={setCountryId}
                options={countries}
                loading={referenceDataLoading}
                placeholder={t("settings.general.countryPlaceholder")}
              />
            </Row>
            <Row label={t("settings.general.language")}>
              <SearchableSelect
                value={languageId}
                onChange={setLanguageId}
                options={languages}
                getOptionLabel={(option) => option?.native_name || option?.name || ""}
                loading={referenceDataLoading}
                placeholder={t("settings.general.languagePlaceholder")}
              />
            </Row>
          </Box>

          <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography sx={{ fontSize: "0.75rem", color: "#9ca3af" }}>
              {t("settings.general.lastLogin", { date: formatDateTime(profile?.last_login) })}
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "#9ca3af" }}>
              {t("settings.general.memberSince", { date: formatDateTime(profile?.created_at) })}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
}
